import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { BASE_URL } from "@/config/api";
import { getAccessToken } from "@/utils/token_functions";
import { secureAxios } from "@/middleware/encryptionMiddleware";

export const searchUserByFieldAsync = createAsyncThunk(
  "user/searchByField",
  async ({ key, value }, { rejectWithValue }) => {
    try {
      const response = await secureAxios.get(
        `${BASE_URL}/v1/user/by-any/${key}/${value}`,
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.msg || error.message);
    }
  },
  {
    meta: {
      requiresNetwork: true,
    },
  },
);

// update user thunk
export const updateUserAsync = createAsyncThunk(
  "user/updateUser",
  async (userData, { rejectWithValue }) => {
    try {
      const token = getAccessToken();
      if (!token) {
        throw new Error("No authentication token found");
      }

      const response = await secureAxios.patch(
        `${BASE_URL}/v1/user`,
        userData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.msg || error.message);
    }
  },
  {
    meta: {
      requiresNetwork: true,
    },
  },
);

// batch fetch action
export const fetchMultipleUsersAsync = createAsyncThunk(
  "user/fetchMultipleUsers",
  async (userIds, { rejectWithValue }) => {
    try {
      const uniqueIds = [...new Set(userIds)]; // Remove duplicates
      const promises = uniqueIds.map((id) =>
        secureAxios.get(`${BASE_URL}/v1/user/by-any/_id/${id}`),
      );

      const responses = await Promise.all(promises);
      const users = responses.map((response) => response.data.data[0]);
      return users;
    } catch (error) {
      return rejectWithValue(error.response?.data?.msg || error.message);
    }
  },
  {
    meta: {
      requiresNetwork: true,
    },
  },
);

// fetch profile thunk
export const fetchUserProfileAsync = createAsyncThunk(
  "user/fetchProfile",
  async (_, { rejectWithValue }) => {
    try {
      const token = getAccessToken();
      if (!token) {
        throw new Error("No authentication token found");
      }

      const response = await secureAxios.get(`${BASE_URL}/v1/auth/profile`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.msg || error.message);
    }
  },
  {
    meta: {
      requiresNetwork: true,
    },
  },
);

// invite registration
export const completeInviteRegistrationAsync = createAsyncThunk(
  "user/completeInviteRegistration",
  async (
    { userId, organizationId, password, confirmPassword },
    { rejectWithValue },
  ) => {
    try {
      const response = await secureAxios.post(
        `${BASE_URL}/v1/user/invite-register/${userId}/${organizationId}`,
        { password, confirmPassword },
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.msg || error.message);
    }
  },
  {
    meta: {
      requiresNetwork: true,
    },
  },
);

export const updateProfileAsync = createAsyncThunk(
  "user/updateProfile",
  async (userData, { rejectWithValue }) => {
    try {
      const token = getAccessToken();
      if (!token) {
        throw new Error("No authentication token found");
      }

      const response = await secureAxios.patch(
        `${BASE_URL}/v1/auth/profile`,
        userData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.msg || error.message);
    }
  },
  {
    meta: {
      requiresNetwork: true,
    },
  },
);

// Fetch user activity: GET /v1/user/{id}/activity
export const fetchUserActivitiesAsync = createAsyncThunk(
  "user/fetchActivities",
  async (userId, { rejectWithValue }) => {
    try {
      const token = getAccessToken();
      if (!token) {
        throw new Error("No authentication token found");
      }

      const response = await secureAxios.get(
        `${BASE_URL}/v1/user/${userId}/activity`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.msg || error.message);
    }
  },
  {
    meta: {
      requiresNetwork: true,
    },
  },
);

const pickListFromPayload = (payload) => {
  // Shape: { message, data: { result, total, skip, limit } }
  if (Array.isArray(payload?.data?.result)) {
    return {
      list: payload.data.result,
      total: payload.data.total ?? payload.data.result.length,
    };
  }
  if (Array.isArray(payload?.result)) {
    return {
      list: payload.result,
      total: payload.total ?? payload.result.length,
    };
  }
  if (Array.isArray(payload?.data)) {
    return { list: payload.data, total: payload.data.length };
  }
  if (Array.isArray(payload)) {
    return { list: payload, total: payload.length };
  }
  return { list: [], total: 0 };
};

const initialState = {
  searchedUser: null,
  isSearching: false,
  searchError: null,
  isUpdating: false,
  updateError: null,
  updateSuccess: false,
  profile: {
    fullname: "",
    email: "",
    phone: "",
    profileImage: "",
    organizationName: "",
    role: "",
  },
  multipleUsers: [],
  isLoadingMultiple: false,
  multipleUsersError: null,
  activities: [],
  activitiesTotal: 0,
  isLoadingActivities: false,
  activitiesError: null,
};

const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    clearUserData: (state) => {
      state.searchedUser = null;
      state.profile = initialState.profile;
    },
    updateProfileData: (state, action) => {
      if (state.searchedUser?.data?.[0]) {
        state.searchedUser.data[0] = {
          ...state.searchedUser.data[0],
          ...action.payload,
        };
        const userData = state.searchedUser.data[0];
        state.profile = {
          fullname: userData.fullname || "",
          email: userData.email || "",
          phone: userData.phone || "",
          profileImage: userData.profileImage || "",
        };
      }
    },
    setMultipleUsers: (state, action) => {
      state.multipleUsers = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(searchUserByFieldAsync.pending, (state) => {
        state.isSearching = true;
        state.searchError = null;
      })
      .addCase(searchUserByFieldAsync.fulfilled, (state, action) => {
        state.isSearching = false;
        state.searchedUser = action.payload;
      })
      .addCase(searchUserByFieldAsync.rejected, (state, action) => {
        state.isSearching = false;
        state.searchError = action.payload;
        state.searchedUser = null;
      })
      .addCase(updateUserAsync.pending, (state) => {
        state.isUpdating = true;
        state.updateError = null;
        state.updateSuccess = false;
      })
      .addCase(updateUserAsync.fulfilled, (state, action) => {
        state.isUpdating = false;
        state.updateSuccess = true;
        if (state.searchedUser?.data?.[0]) {
          state.searchedUser.data[0] = {
            ...state.searchedUser.data[0],
            ...action.payload.data,
          };
          const userData = state.searchedUser.data[0];
          state.profile = {
            fullname: userData.fullname || "",
            email: userData.email || "",
            phone: userData.phone || "",
            profileImage: userData.profileImage || "",
          };
        }
      })
      .addCase(updateUserAsync.rejected, (state, action) => {
        state.isUpdating = false;
        state.updateError = action.payload;
        state.updateSuccess = false;
      })
      .addCase(fetchMultipleUsersAsync.pending, (state) => {
        state.isLoadingMultiple = true;
        state.multipleUsersError = null;
      })
      .addCase(fetchMultipleUsersAsync.fulfilled, (state, action) => {
        state.isLoadingMultiple = false;
        state.multipleUsers = action.payload;
      })
      .addCase(fetchMultipleUsersAsync.rejected, (state, action) => {
        state.isLoadingMultiple = false;
        state.multipleUsersError = action.payload;
      })
      .addCase(fetchUserProfileAsync.pending, (state) => {
        state.isSearching = true;
        state.searchError = null;
      })
      .addCase(fetchUserProfileAsync.fulfilled, (state, action) => {
        state.isSearching = false;
        const payload = action.payload;
        const userData =
          payload?.data && !Array.isArray(payload.data)
            ? payload.data
            : Array.isArray(payload?.data)
              ? payload.data[0]
              : payload;

        if (!userData) return;

        state.searchedUser = { data: [userData] };
        state.profile = {
          fullname: userData.fullname || "",
          email: userData.email || "",
          phone: userData.phone || "",
          profileImage: userData.profileImage || "",
          organizationName: userData.roles?.[0]?.organizationID?.name || "",
          role: userData.roles?.[0]?.name || "User",
        };
      })
      .addCase(fetchUserProfileAsync.rejected, (state, action) => {
        state.isSearching = false;
        state.searchError = action.payload;
        state.searchedUser = null;
      })
      .addCase(completeInviteRegistrationAsync.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(completeInviteRegistrationAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.data || {};
        state.isLoggedIn = true;
        state.error = null;
      })
      .addCase(completeInviteRegistrationAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(updateProfileAsync.pending, (state) => {
        state.isUpdating = true;
        state.updateError = null;
        state.updateSuccess = false;
      })
      .addCase(updateProfileAsync.fulfilled, (state, action) => {
        state.isUpdating = false;
        state.updateSuccess = true;

        const userData = action.payload.data;
        state.profile = {
          ...state.profile,
          fullname: userData.fullname || state.profile.fullname,
          email: userData.email || state.profile.email,
          phone: userData.phone || state.profile.phone,
          profileImage: userData.profileImage || state.profile.profileImage,
        };

        if (state.searchedUser?.data?.[0]) {
          state.searchedUser.data[0] = {
            ...state.searchedUser.data[0],
            ...userData,
          };
        }
      })
      .addCase(updateProfileAsync.rejected, (state, action) => {
        state.isUpdating = false;
        state.updateError = action.payload;
        state.updateSuccess = false;
      })
      .addCase(fetchUserActivitiesAsync.pending, (state) => {
        state.isLoadingActivities = true;
        state.activitiesError = null;
      })
      .addCase(fetchUserActivitiesAsync.fulfilled, (state, action) => {
        state.isLoadingActivities = false;
        const { list, total } = pickListFromPayload(action.payload);
        state.activities = list;
        state.activitiesTotal = total;
      })
      .addCase(fetchUserActivitiesAsync.rejected, (state, action) => {
        state.isLoadingActivities = false;
        state.activitiesError = action.payload;
        state.activities = [];
        state.activitiesTotal = 0;
      });
  },
});

export const { clearUserData, updateProfileData, setMultipleUsers } =
  userSlice.actions;

// Selectors
export const selectSearchedUser = (state) => state.user.searchedUser;
export const selectIsSearching = (state) => state.user.isSearching;
export const selectSearchError = (state) => state.user.searchError;
export const selectIsUpdating = (state) => state.user.isUpdating;
export const selectUpdateError = (state) => state.user.updateError;
export const selectUpdateSuccess = (state) => state.user.updateSuccess;
export const selectUserProfile = (state) => state.user.profile;
export const selectMultipleUsers = (state) => state.user.multipleUsers || [];
export const selectIsLoadingMultiple = (state) => state.user.isLoadingMultiple;
export const selectMultipleUsersError = (state) =>
  state.user.multipleUsersError;
export const selectUpdateProfileSuccess = (state) => state.user.updateSuccess;
export const selectUserActivities = (state) => state.user.activities || [];
export const selectIsLoadingActivities = (state) =>
  state.user.isLoadingActivities;

export const selectRawUserData = (state) =>
  state.user.searchedUser?.data?.[0] || null;

// Notification preferences object: { email, push, types, frequency }
export const selectNotificationPreferences = (state) =>
  state.user.searchedUser?.data?.[0]?.notificationPreferences || null;

// Whether push notifications are enabled at the device level
export const selectIsPushEnabled = (state) =>
  state.user.searchedUser?.data?.[0]?.isPushEnabled ?? false;

export default userSlice.reducer;
